package org.zq.astrock_ai.ui.research.gushi.data

import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.zq.astrock_ai.api.dto.QuantScanItemDto
import org.zq.astrock_ai.api.dto.QuantScanResponseDto
import org.zq.astrock_ai.storage.QuantScanHistoryStorage
import org.zq.astrock_ai.util.currentTimeMillis

@Serializable
data class ScanHistoryChangeDto(
    val code: String,
    val name: String = "",
    val kind: String,
)

@Serializable
data class ScanHistoryItemDto(
    val code: String,
    val name: String = "",
    val signal: String,
)

@Serializable
data class ScanHistorySnapshot(
    val atMs: Long,
    val strategyId: String = "consensus",
    val buyCount: Int = 0,
    val sellCount: Int = 0,
    val holdCount: Int = 0,
    val changeCount: Int = 0,
    val changes: List<ScanHistoryChangeDto> = emptyList(),
    /** 仅保留买/卖，控制体积 */
    val highlights: List<ScanHistoryItemDto> = emptyList(),
)

@Serializable
private data class ScanHistoryFile(
    val version: Int = 1,
    val entries: List<ScanHistorySnapshot> = emptyList(),
)

object ScanHistoryStore {
    private const val MAX_ENTRIES = 20
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
    }

    fun load(): List<ScanHistorySnapshot> =
        runCatching {
            val raw = QuantScanHistoryStorage.load() ?: return emptyList()
            json.decodeFromString<ScanHistoryFile>(raw).entries
        }.getOrDefault(emptyList())

    fun append(
        result: QuantScanResponseDto,
        changes: Map<String, ScanChangeKind>,
        nameByCode: Map<String, String> = emptyMap(),
    ): ScanHistorySnapshot {
        val snapshot = ScanHistorySnapshot(
            atMs = currentTimeMillis(),
            strategyId = result.strategyId.ifBlank { "consensus" },
            buyCount = result.buyCount,
            sellCount = result.sellCount,
            holdCount = result.holdCount,
            changeCount = changes.size,
            changes = changes.entries.map { (code, kind) ->
                ScanHistoryChangeDto(
                    code = code,
                    name = nameByCode[code]
                        ?: result.items.firstOrNull { it.code == code }?.name.orEmpty(),
                    kind = kind.name,
                )
            },
            highlights = result.items
                .filter { it.signal == "BUY" || it.signal == "SELL" }
                .map {
                    ScanHistoryItemDto(
                        code = it.code,
                        name = it.name.ifBlank { nameByCode[it.code].orEmpty() },
                        signal = it.signal,
                    )
                },
        )
        val next = (listOf(snapshot) + load()).take(MAX_ENTRIES)
        QuantScanHistoryStorage.save(
            json.encodeToString(ScanHistoryFile(entries = next)),
        )
        return snapshot
    }
}

fun scanChangeKindFromName(name: String): ScanChangeKind? =
    ScanChangeKind.entries.firstOrNull { it.name == name }

fun formatScanHistoryTime(atMs: Long): String {
    val totalSec = (atMs / 1000L).coerceAtLeast(0L)
    val secInDay = (totalSec % 86_400L).toInt()
    val hour = secInDay / 3600
    val minute = (secInDay % 3600) / 60
    val (month, day) = utcMonthDay(totalSec)
    return "${pad2(month)}-${pad2(day)} ${pad2(hour)}:${pad2(minute)}"
}

private fun utcMonthDay(totalSec: Long): Pair<Int, Int> {
    var days = (totalSec / 86_400L).toInt()
    var year = 1970
    while (true) {
        val diy = if (isLeap(year)) 366 else 365
        if (days < diy) break
        days -= diy
        year++
        if (year > 2100) break
    }
    val mdays = intArrayOf(
        31,
        if (isLeap(year)) 29 else 28,
        31, 30, 31, 30, 31, 31, 30, 31, 30, 31,
    )
    var month = 1
    for (m in mdays) {
        if (days < m) break
        days -= m
        month++
    }
    return month to (days + 1)
}

private fun isLeap(year: Int): Boolean =
    (year % 4 == 0 && year % 100 != 0) || year % 400 == 0

private fun pad2(n: Int): String = n.toString().padStart(2, '0')

fun buildScanAlertText(
    changes: Map<String, ScanChangeKind>,
    items: List<QuantScanItemDto>,
): String {
    if (changes.isEmpty()) return ""
    val nameOf = items.associate { it.code to it.name }
    val parts = changes.entries.take(4).map { (code, kind) ->
        val name = nameOf[code].orEmpty().ifBlank { code }
        "$name${kind.label}"
    }
    val more = changes.size - parts.size
    return buildString {
        append(parts.joinToString(" · "))
        if (more > 0) append(" 等${changes.size}项变化")
    }
}
